/**
 * Template Pages AI generation. UDS CRUD lives in admin hooks via `@/graphql`.
 */

import { queryBlocksAgent } from '@/lib/blocks-ai-agent';
import type { LandingGenerationResult } from '../templates/types/landing-page.types';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';

const LANDING_PAGE_AGENT_ID =
  import.meta.env.VITE_LANDING_PAGE_AGENT_ID || 'd545016271c14c7cbc87d5da6a7a54a5';

interface GenerateArgs {
  template: TemplateWithScreens;
  locale: string;
  otherTemplates?: Array<{ slug: string; title: string; description?: string | null }>;
  signal?: AbortSignal;
}

/**
 * Compact brand guide passed in the user prompt. The agent's full
 * server-side system prompt covers brand voice in more depth — this is
 * the abbreviated version the old route also sent inline (see
 * `BRAND_GUIDE` in the old generate route's `loadBrandGuide` fallback).
 */
const BRAND_GUIDE_EXCERPT = `Follow SELISE brand tone: professional, clear, approachable. Use Tailwind with CSS variables (no hex). Emphasize: free, no-login, no-payment, Swiss privacy, instant PDF download. Avoid absolute claims ("100% compliance", "guaranteed"). Use measured, trustworthy phrasing.`;

const buildUserPrompt = (args: GenerateArgs): string => {
  const { template, locale, otherTemplates = [] } = args;

  const context = {
    templateTitle: template.title,
    templateDescription: template.description,
    slug: template.slug,
    locale,
    keywords: template.keywords || [],
    screens: (template.screens ?? []).map((s) => ({
      title: s.title,
      description: s.description,
      type: s.type,
      enableApplyStandards: s.enableApplyStandards ?? false,
      fields: (s.fields ?? []).map((f) => ({
        name: f.name,
        label: f.label,
        type: f.type,
        required: f.required,
        placeholder: f.placeholder,
        helpText: f.helpText,
        options: f.options,
      })),
    })),
  };

  const linksList = otherTemplates
    .map((t) => `   - "${t.title}": /${locale}/templates/${t.slug}`)
    .join('\n');

  return `Generate a COMPREHENSIVE, long-form, SEO-focused landing page for the template described below.

TEMPLATE WORKFLOW UNDERSTANDING:
The template works through a guided question flow. Users are asked simple, legally sophisticated questions in plain, human-understandable language. We explain every step clearly, suggest what to do or which option to select (with easy-to-understand explanations), but the user has full control (human in the loop). The process is designed to be accessible to anyone, not just legal professionals. Based on the template context provided below, explain exactly how this specific template's guided question flow works.

SECTION GUIDE (include ALL required sections, plus additional sections as needed for a comprehensive long-form page):

REQUIRED SECTIONS:
1. hero - Compelling headline, subtitle with value proposition emphasizing free, no-login, Swiss privacy. Include 4-6 bullet points highlighting key benefits. Must include primary CTA (#generate).

2. featureGrid - 6-9 key features/benefits with detailed descriptions (2-3 sentences each). Focus on what makes this template valuable, easy to use, and legally sound. Emphasize the guided question approach and user control.

3. steps - 4-6 step process showing how the template works. This is CRITICAL: Explain the guided question flow in simple terms. Describe how users will be asked questions, how we explain each step in plain language, how we suggest options but users have full control. Make it clear this is a human-in-the-loop process where users make decisions at each step. Base this on the actual screens and fields in the template context.

4. richText - 2-3 paragraphs (or more) of detailed content explaining the template's value, use cases, and benefits. Use simple language, avoid jargon. Explain when and why someone would need this template.

5. stats - 3-4 relevant statistics or metrics (can be industry stats or template benefits). Make these meaningful and contextual.

6. richText - Another section with detailed content about best practices, tips, or important considerations. Keep language accessible.

7. faq - 10-15 comprehensive Q&As covering common questions, legal considerations, use cases, the free nature of the service, privacy (no data storage), and how the guided process works. Answer in simple, understandable language.

8. cta - Strong closing CTA with compelling copy emphasizing free download, no commitment, instant PDF access.

OPTIONAL ADDITIONAL SECTIONS (add these if they help reach the 1500-2500 word goal and provide value):
- Additional richText blocks with deep dives into specific use cases, legal context (in simple terms), or industry insights
- Additional featureGrid blocks if there are more benefits to highlight
- Media blocks if visual explanations would help
- Additional FAQ sections if more questions need answering

Generate substantial content for each section - no placeholder or minimal text. Aim for a comprehensive, long-form landing page that thoroughly explains the template and its value.

BRAND GUIDE (abbreviated):
${BRAND_GUIDE_EXCERPT}

TEMPLATE CONTEXT (JSON):
${JSON.stringify(context, null, 2)}

MANDATORY CTAs:
- Primary CTA href: "#generate" (renderer will resolve to current page + /generate)
- Secondary CTA href: "/${locale}/templates"

Return strict JSON with this shape (no additional fields):
{
  "title": string,
  "description": string,
  "ogTitle": string,
  "ogDescription": string,
  "ogImage": null,
  "keywords": string[],
  "blocks": [
    {
      "type": "hero",
      "eyebrow"?: string,
      "title": string,
      "subtitle"?: string,
      "bullets"?: string[],
      "primaryCta"?: { "label": string, "href": string, "variant"?: "primary"|"secondary"|"ghost" },
      "secondaryCta"?: { "label": string, "href": string, "variant"?: "primary"|"secondary"|"ghost" },
      "imageUrl"?: string,
      "imageAlt"?: string
    },
    {
      "type": "featureGrid",
      "title"?: string,
      "description"?: string,
      "columns"?: number,
      "features": [{ "title": string, "description"?: string, "icon"?: string }]
    },
    {
      "type": "stats",
      "title"?: string,
      "description"?: string,
      "items": [{ "label": string, "value": string, "subtext"?: string }]
    },
    {
      "type": "steps",
      "title"?: string,
      "description"?: string,
      "items": [{ "title": string, "description"?: string, "icon"?: string }]
    },
    {
      "type": "media",
      "title"?: string,
      "description"?: string,
      "imageUrl": string,
      "imageAlt"?: string
    },
    {
      "type": "richText",
      "title"?: string,
      "body": string[]
    },
    {
      "type": "faq",
      "title"?: string,
      "items": [{ "question": string, "answer": string }]
    },
    {
      "type": "testimonials",
      "title"?: string,
      "items": [{ "quote": string, "name": string, "role"?: string, "avatarUrl"?: string }]
    },
    {
      "type": "cta",
      "title": string,
      "description"?: string,
      "primaryCta": { "label": string, "href": "#generate", "variant"?: "primary"|"secondary"|"ghost" },
      "secondaryCta"?: { "label": string, "href": string, "variant"?: "primary"|"secondary"|"ghost" }
    }
  ]
}

STRICT LINKING RULES - CRITICAL:
You MUST ONLY use these allowed links. DO NOT create any other links, especially NOT:
- Links to "examples", "samples", "template examples", "see examples", "view samples", or similar
- Links to non-existent pages or features
- Links to external sites except the two approved SELISE domains below

ALLOWED LINKS ONLY:
1. Internal template links (use markdown format [Link Text](url) in text content):
Available templates:
${linksList || '   (no other templates available for cross-linking)'}
   - "All templates" or "Browse templates": /${locale}/templates

2. Primary CTA (always use this):
   - #generate

3. External SELISE domains (only these two):
   - https://selisesignature.com/
   - https://selisegroup.com/

LINKING GUIDELINES:
- Add 2-5 internal template links naturally within richText, faq answers, or descriptions where contextually relevant
- Only link where it makes sense (don't force links)
- Use varied anchor text (template name, action phrases like "create your NDA", "generate a contract")
- For internal template links, use markdown format: [anchor text](/${locale}/templates/slug)
- For external links, use markdown format: [anchor text](https://selisesignature.com/) or [anchor text](https://selisegroup.com/)
- href must be absolute (http/https), or start with "/", or start with "#"
- NEVER create links to pages that don't exist (no examples, samples, galleries, etc.)

LANGUAGE RULES:
- Avoid exaggerated/absolute claims (e.g., "100% compliance", "guaranteed results"). Use measured, trustworthy phrasing.
- Emphasize throughout: Free, no login, no payment, no data storage, Swiss privacy standards, instant PDF download
- Explain the guided question process in simple terms - users are asked questions, we explain each step clearly, suggest options, but users have full control`;
};

/**
 * Best-effort JSON extraction from the agent's reply.
 *
 * Agents sometimes wrap their JSON in prose ("Here's the landing page:
 * { ... }") or fenced code blocks. We try strict parse first, then
 * strip code fences, then fall back to the first `{ … }` substring.
 * jsonrepair isn't installed in this project so we keep the parsing
 * pure-JS and small.
 */
function parseAgentJson(raw: string): unknown {
  // 1) strict
  try {
    return JSON.parse(raw);
  } catch {
    // continue
  }

  // 2) strip a leading/trailing ```json fence (and any other fence)
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (fenced && fenced[1]) {
    try {
      return JSON.parse(fenced[1]);
    } catch {
      // continue
    }
  }

  // 3) first balanced-ish {…} substring — naive but adequate for an
  // SSE reply that's been wrapped in conversational text.
  const firstBrace = raw.indexOf('{');
  const lastBrace = raw.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    const slice = raw.slice(firstBrace, lastBrace + 1);
    return JSON.parse(slice);
  }

  throw new Error('Agent reply did not contain JSON.');
}

export const generateLandingViaAgent = async (
  args: GenerateArgs
): Promise<LandingGenerationResult> => {
  const message = buildUserPrompt(args);
  const response = await queryBlocksAgent(LANDING_PAGE_AGENT_ID, message, args.signal);

  if (!response || !response.message) {
    throw new Error('AI agent returned an empty response.');
  }

  // `response.message` may itself be a JSON-stringified payload or
  // already-parsed object — `queryBlocksAgent`'s typed return says
  // string, but agents have been known to return objects through SSE
  // serialization. Handle both.
  const rawMessage =
    typeof response.message === 'string' ? response.message : JSON.stringify(response.message);

  let parsed: unknown;
  try {
    parsed = parseAgentJson(rawMessage);
  } catch (error) {
    console.error('[generateLandingViaAgent] failed to parse agent reply', error);
    throw new Error('The AI response could not be parsed as JSON. Please try generating again.');
  }

  const result = parsed as Partial<LandingGenerationResult>;
  if (!result || typeof result !== 'object' || !Array.isArray(result.blocks)) {
    throw new Error('The AI response did not match the expected landing-page shape.');
  }

  return {
    title: result.title ?? '',
    description: result.description ?? '',
    ogTitle: result.ogTitle ?? result.title ?? '',
    ogDescription: result.ogDescription ?? result.description ?? '',
    ogImage: result.ogImage ?? null,
    keywords: result.keywords ?? [],
    blocks: result.blocks,
  };
};
